import { AppLanguage } from '../model/Types';

/**
 * All UI languages supported by the application.
 * The first entry is treated as the default language and may be served without a URL prefix.
 */
export const SUPPORTED_LANG_CODES = ['sr', 'en', 'de', 'fr', 'ru'] as const;

export type SupportedLangCode = (typeof SUPPORTED_LANG_CODES)[number];

/**
 * Default language — served both without a prefix (/trps) and with (/sr/trps).
 * All other languages require the prefix (/en/trps, /de/trps, ...).
 */
export const DEFAULT_LANG: SupportedLangCode = 'sr';

export const LANGS_WITH_PREFIX: SupportedLangCode[] = SUPPORTED_LANG_CODES.filter(
    (l) => l !== DEFAULT_LANG,
) as SupportedLangCode[];

export const isSupportedLang = (value: unknown): value is SupportedLangCode =>
    typeof value === 'string' &&
    (SUPPORTED_LANG_CODES as readonly string[]).includes(value);

/**
 * Numeric language IDs used by the backend (`therapistLanguages[].langId`,
 * `/therapist/{id}/{languageId}` API path, etc.).
 *
 *   en -> 1
 *   sr -> 2
 *   de -> 3
 *   fr -> 4
 *   ru -> 5
 */
const LANG_CODE_TO_ID: Record<SupportedLangCode, number> = {
    en: 1,
    sr: 2,
    de: 3,
    fr: 4,
    ru: 5,
};

export const langCodeToLangId = (code: SupportedLangCode): number => LANG_CODE_TO_ID[code];

/**
 * Map between URL code ('sr', 'en', ...) and internal AppLanguage enum ('SR', 'EN', ...).
 */
export const langCodeToAppLanguage = (code: SupportedLangCode): AppLanguage =>
    code.toUpperCase() as AppLanguage;

export const appLanguageToLangCode = (lang: AppLanguage): SupportedLangCode =>
    lang.toLowerCase() as SupportedLangCode;

/**
 * Extract the language code from a pathname.
 * Returns the code when the first path segment is a supported non-default lang,
 * otherwise returns DEFAULT_LANG.
 *
 * Examples (DEFAULT_LANG = 'sr'):
 *   '/'                 -> 'sr'
 *   '/trps'             -> 'sr'
 *   '/sr'               -> 'sr'
 *   '/sr/trps'          -> 'sr'
 *   '/en'               -> 'en'
 *   '/en/trps'          -> 'en'
 *   '/de/blog/foo'      -> 'de'
 *   '/somethingElse'    -> 'sr' (default, no prefix match)
 */
export const extractLangFromPath = (pathname: string): SupportedLangCode => {
    const segments = pathname.split('/').filter(Boolean);
    const first = segments[0];
    if (first && isSupportedLang(first)) {
        return first;
    }
    return DEFAULT_LANG;
};

/**
 * Strip the leading language segment from a pathname if it matches a supported lang.
 * Always returns a path starting with '/'.
 */
export const stripLangFromPath = (pathname: string): string => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] && isSupportedLang(segments[0])) {
        segments.shift();
    }
    return '/' + segments.join('/');
};

/**
 * Build a localized absolute path for the given language.
 * Default language (sr) keeps the bare path; other languages get the lang prefix.
 *
 *   buildLocalizedPath('/trps', 'sr') -> '/trps'
 *   buildLocalizedPath('/trps', 'en') -> '/en/trps'
 *   buildLocalizedPath('/', 'de')     -> '/de'
 */
export const buildLocalizedPath = (path: string, lang: SupportedLangCode): string => {
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    if (lang === DEFAULT_LANG) {
        return cleanPath;
    }
    if (cleanPath === '/') {
        return `/${lang}`;
    }
    return `/${lang}${cleanPath}`;
};
